import { Injectable } from '@angular/core';
import {
  ANALYTICS_EVENTS,
  EVENT_CLICK_TYPE,
  HEADER_MENU,
  USER_LOGGED_IN_STATUS
} from '../../constants/utils';
import {
  AnalyticsSiteAssets,
  ClickEventAnalytics
} from '../../models/utils/analytics.interfaces';
import { ContentBodyExtendedAdobeAnalyticsService } from './content-body-extended-adobe-analytics.service';

@Injectable({
  providedIn: 'root'
})
export class ContentBodyCoreAdobeAnalyticsUtil extends ContentBodyExtendedAdobeAnalyticsService {
  setHomePageClickEventAnalytics(cardDiv: Element, eventDetail: string): void {
    if (!cardDiv) return;
    cardDiv.addEventListener('click', (event) =>
      this.handleCardClick(event, eventDetail)
    );
  }

  handleCardClick(event: Event, eventDetail: string): void {
    const clickedElement = event.target as HTMLElement;
    const closestAnchor = clickedElement.closest('a');
    if (!closestAnchor) return;
    event.preventDefault();
    const contentInfo = this.getContentInfo(clickedElement);
    const eventType = closestAnchor.getAttribute('href') ? 'link' : event.type;
    const fullUrl = this.getFullUrl(closestAnchor);
    this.handleAnalytics(contentInfo, eventType, fullUrl, eventDetail);
    window.location.href = fullUrl;
  }

  handleAnalytics(
    contentInfo: { clicked: string; parent?: string },
    eventType: string,
    fullUrl: string,
    eventDetail: string
  ): void {
    this.setHomePageClickAnalytics(
      contentInfo,
      eventType,
      fullUrl,
      eventDetail
    );
  }

  getFullUrl(anchor: HTMLAnchorElement): string {
    return new URL(anchor.getAttribute('href') || '', window.location.origin)
      .href;
  }

  setHomePageClickAnalytics(
    contentInfo: { clicked: string; parent?: string },
    eventType: string,
    fullUrl: string,
    eventDetail: string
  ): void {
    const text = this.getComponentName(eventDetail);
    const data: ClickEventAnalytics = {
      event: ANALYTICS_EVENTS.CLICK,
      event_detail: eventDetail,
      click: {
        name: contentInfo.clicked,
        type: eventType,
        component_type: text,
        component_name: contentInfo.parent ?? contentInfo.clicked,
        page_section: HEADER_MENU.HOMEPAGE.PAGE_SECTION + text,
        action: fullUrl
      },
      user: {
        attributes: {
          is_logged_in: this.isLoggedInGlobal
            ? USER_LOGGED_IN_STATUS.AUTHENTICATED
            : USER_LOGGED_IN_STATUS.UNAUTHENTICATED
        }
      }
    };
    this.analyticsService.setAnalytics(data);
  }

  setHomePageSiteAssetsAnalytics(
    cardDiv: Element,
    eventDetail: string
  ): AnalyticsSiteAssets[] {
    if (!cardDiv) return [];
    const relevantElements = Array.from(
      cardDiv.querySelectorAll('h3, span, a')
    ).filter((element) => {
      const hasText = element.textContent?.trim();
      const isRelevantClass = [
        'card-headertxt',
        'card-title brand-head-home'
      ].includes(element.className);
      return hasText && isRelevantClass;
    });
    const component_type = this.getComponentName(eventDetail) || '';
    const newAssets = relevantElements.map((element, index) => ({
      name: element.textContent?.trim() || '',
      type: component_type,
      position: `${index + 1} of ${relevantElements.length}`,
      id: this.computePageLoadAssetID(component_type, index)
    }));
    return newAssets;
  }

  setLoginGlobalStatus(loggedInStatus: boolean, errorCode?: string): void {
    this.isLoggedInGlobal = loggedInStatus;
    const eventDetail = this.getLoggedInStatus(this.isLoggedInGlobal);
    const data: ClickEventAnalytics = {
      event: ANALYTICS_EVENTS.CLICK,
      event_detail:
        eventDetail === USER_LOGGED_IN_STATUS.LOGIN_FAILURE
          ? 'log-in-failure'
          : eventDetail,
      click: {
        name: this.getLoggedInStatus(this.isLoggedInGlobal),
        type: EVENT_CLICK_TYPE.BUTTON,
        page_section: HEADER_MENU.LOGIN.PAGE_SECTION,
        action: this.getLoggedInStatus(this.isLoggedInGlobal)
      },
      user: {
        attributes: {
          is_logged_in: this.isLoggedInGlobal
            ? USER_LOGGED_IN_STATUS.AUTHENTICATED
            : USER_LOGGED_IN_STATUS.UNAUTHENTICATED
        }
      },
      ...(eventDetail === USER_LOGGED_IN_STATUS.LOGIN_FAILURE && {
        error: {
          type: 'home page load error',
          code: errorCode,
          message: USER_LOGGED_IN_STATUS.LOGIN_AUTHENTICATION_FAILED
        }
      })
    };
    this.analyticsService.setAnalytics(data);
  }

  getLoggedInStatus(loggedInStatus: boolean): string {
    return loggedInStatus
      ? USER_LOGGED_IN_STATUS.LOGIN_SUCCESS
      : USER_LOGGED_IN_STATUS.LOGIN_FAILURE;
  }
}
